import React from 'react'
import { NumericFormat } from 'react-number-format';
import ErrorMessage from './ErrorMessage';
import Label from './Label';
import { useFieldsLocked } from '../../contexts/ReadOnlyContext'

export default function CurrencyField({
  addon,
  addonPosition = 'start',
  autoFocus,
  error,
  label,
  max,
  min,
  name,
  noRightPadding,
  noLabelPadding,
  onBlur,
  onChange = () => {},
  optional,
  placeholder,
  showAsterisk,
  tooltip,
  type='text',
  thousandSeparator = '.',
  displayType = "input",
  decimalSeparator = ",",
  thousandsGroupStyle = "thousand",
  value = undefined,
  defaultValue= undefined,
  className,
  overrideClassName = '',
  topMargin = 'mt-5',
  disabled: disabledProp,
  maxLength,
  autoComplete,
  setInitialValue = () => {},
  onAddonClick = null,
  addonColor = 'text-gray-500',
  setFieldValue = () => {},
  additionalAction,
  labelMarginBottom,
  decimalScale = undefined,
  fixedDecimalScale = false,
  allowNegative = true,
  valueIsNumericString = false
}) {
  const fieldsLocked = useFieldsLocked()
  const disabled = disabledProp || fieldsLocked
  return (
    <div className={topMargin}>
      <Label 
        label={label} 
        noRightPadding={noRightPadding} 
        noLabelPadding={noLabelPadding}
        showAsterisk={showAsterisk}
        tooltip={tooltip}
        marginBottom={labelMarginBottom}
      />

      <div className={`
        flex items-center
        rounded-lg overflow-hidden
        border-1 border-rb-lite-grey dark:border-rb-dark-mode-lighter
        bg-white dark:bg-rb-dark-mode
        focus-within:border-rb-green focus-within:border-2
        ${error ? 'border-red-500' : ''}
        ${disabled ? 'rb-disabled' : ''}
      `}>
        {addon && addonPosition === 'start' && (
          <span
            className={`ml-1 pr-1 ${addonColor} select-none leading-none ${onAddonClick ? 'cursor-pointer' : ''}`}
            onClick={onAddonClick}
            style={{paddingTop: '1px'}}
          >
            {addon}
          </span>
        )}

        <NumericFormat
          autoFocus={autoFocus}
          className={`
            ${className ? className : 'px-4'}
            h-11 w-full
            bg-transparent
            text-rb-black dark:text-rb-dark-mode-text-light
            placeholder-rb-mat-grey dark:placeholder-rb-dark-mode-text-grey
            focus-visible:outline-0
            focus:outline-0
            focus:ring-transparent
            focus:ring-offset-transparent
            ring-transparent
            ring-offset-transparent
            border-0
            leading-none
            ${overrideClassName}
            ${addonPosition === 'start' ? 'pl-1' : 'pl-4'}
            ${addonPosition === 'end' ? 'pr-2' : 'pr-4'}
          `}
          min={min}
          max={max}
          name={name}
          onBlur={onBlur}
          placeholder={placeholder}
          type={type}
          value={value}
          defaultValue={defaultValue}
          disabled={disabled}
          maxLength={maxLength}
          autoComplete={autoComplete}
          thousandsGroupStyle={thousandsGroupStyle}
          thousandSeparator={thousandSeparator}
          decimalSeparator={decimalSeparator}
          displayType={displayType}
          decimalScale={decimalScale}
          fixedDecimalScale={fixedDecimalScale}
          allowNegative={allowNegative}
          valueIsNumericString={valueIsNumericString}
          onValueChange={({ value, formattedValue, floatValue }) => {
            // Update the field value for form handling - use floatValue if available, fallback to value
            const numericValue = floatValue !== undefined ? floatValue : (value || 0)
            setFieldValue(name, numericValue, true)
            // Call the onChange handler with the expected event format
            // Use string representation to preserve decimals
            const valueToUse = floatValue !== undefined ? floatValue.toString() : value
            const syntheticEvent = {
              target: {
                name: name,
                value: valueToUse
              }
            }
            setInitialValue(syntheticEvent)
            onChange(syntheticEvent)
            // Call additional action if provided
            if(additionalAction && typeof additionalAction === 'function') {
              additionalAction(numericValue, name)
            }
          }}
        />

        {addon && addonPosition === 'end' && (
          <span
            className={`mr-2 pl-2 ${addonColor} select-none leading-none ${onAddonClick ? 'cursor-pointer' : ''}`}
            style={{paddingTop: '1px'}}
            onClick={onAddonClick}
          >
            {addon}
          </span>
        )}
        {(optional && type !== 'number' || (optional && !value && value != 0 && type === 'number')) && <p className="optional-text">Optional</p>}
      </div>
      {
        error && (
          <ErrorMessage text={error} />
        )
      }
    </div>
  )
}